import { sessionExpiredEvent } from "./route-auth";

const runtimeEnv =
  (
    import.meta as ImportMeta & {
      env?: Record<string, string | boolean | undefined>;
    }
  ).env ?? {};
export const API_BASE = (runtimeEnv.VITE_API_BASE || "/api").replace(/\/$/, "");
const AUTH_BASE = (runtimeEnv.VITE_AUTH_BASE || "/auth").replace(/\/$/, "");

export const authUrl = (path: string) =>
  `${AUTH_BASE}/${path.replace(/^\//, "")}`;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details: string[] = [],
  ) {
    super(message);
  }
}

function formatApiError(error: unknown): string | undefined {
  if (typeof error === "string" && error.trim()) return error;
  if (!error || typeof error !== "object") return undefined;

  const value = error as {
    message?: unknown;
    issues?: unknown;
  };
  if (typeof value.message === "string" && value.message.trim())
    return value.message;
  if (!Array.isArray(value.issues)) return undefined;

  const messages = value.issues.flatMap((issue) => {
    if (!issue || typeof issue !== "object") return [];
    const item = issue as { path?: unknown; message?: unknown };
    if (typeof item.message !== "string" || !item.message.trim()) return [];
    const path = Array.isArray(item.path)
      ? item.path.filter((part): part is string => typeof part === "string")
      : [];
    return [path.length ? `${path.join(".")}: ${item.message}` : item.message];
  });
  return messages.length ? messages.join("\n") : undefined;
}

type ApiRequestInit = RequestInit & {
  notifyDataChange?: boolean;
};

export async function api<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const { notifyDataChange = true, ...requestInit } = init ?? {};
  const response = await fetch(`${API_BASE}${path}`, {
    ...requestInit,
    credentials: "include",
    headers: {
      ...(requestInit.body instanceof FormData
        ? {}
        : { "content-type": "application/json" }),
      ...requestInit.headers,
    },
  });
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/public/"))
      window.dispatchEvent(new Event(sessionExpiredEvent));
    const body = (await response.json().catch(() => ({}))) as {
      error?: unknown;
      details?: unknown;
    };
    const details = Array.isArray(body.details)
      ? body.details.filter((item): item is string => typeof item === "string")
      : [];
    throw new ApiError(
      [
        formatApiError(body.error) ?? `Request failed (${response.status})`,
        ...details,
      ].join("\n"),
      response.status,
      details,
    );
  }
  if (response.status === 204) return undefined as T;
  if (
    notifyDataChange &&
    !["GET", "HEAD", "OPTIONS"].includes(init?.method?.toUpperCase() ?? "GET")
  )
    window.dispatchEvent(new Event("lumen:data-changed"));
  return response.json();
}

export const dataEventsUrl = () => `${API_BASE}/data-events`;
